/**
 * organizationService.ts — Unified service for organization admin domain.
 *
 * Centralizes: members, departments, roles (cargos), hierarchy, scope.
 * Single source of truth for all admin pages.
 */

import { supabase } from '../lib/supabaseClient';

/* =========================================================
   TYPES
========================================================= */

export type RoleType = 'operational' | 'lead' | 'manager' | 'director';

export interface OrgMember {
  user_id: string;
  user_name: string;
  user_email: string;
  role: string | null;
  role_type: RoleType;
  role_id: string | null;
  role_name: string | null;
  department_id: string | null;
  department_name: string | null;
  reports_to_user_id: string | null;
  reports_to_name: string | null;
  subordinate_count: number;
  active_task_count: number;
  late_task_count: number;
}

export interface OrgDepartment {
  id: string;
  organization_id: string;
  name: string;
  color: string;
  parent_department_id: string | null;
  created_at: string;
}

export interface OrgRole {
  id: string;
  organization_id: string;
  role_key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  department_id: string | null;
  created_at: string;
  updated_at: string;
}

export const ROLE_TYPE_OPTIONS: { value: RoleType; label: string }[] = [
  { value: 'operational', label: 'Analista' },
  { value: 'lead', label: 'Coordenador' },
  { value: 'manager', label: 'Gerente' },
  { value: 'director', label: 'Diretor' },
];

export const ROLE_TYPE_LABELS: Record<RoleType, string> = {
  operational: 'Analista',
  lead: 'Coordenador',
  manager: 'Gerente',
  director: 'Diretor',
};

export const SCOPE_DESCRIPTIONS: Record<RoleType, string> = {
  operational: 'Vê apenas suas próprias tarefas',
  lead: 'Vê tarefas da sua equipe (subordinados diretos)',
  manager: 'Vê todas as tarefas do departamento',
  director: 'Vê todas as tarefas da organização',
};

/* =========================================================
   MEMBERS
========================================================= */

export async function getMembers(orgId: string): Promise<OrgMember[]> {
  // 1. Core membership data
  const { data: members, error } = await supabase
    .from('user_organizations')
    .select('user_id, role, role_type, role_id, department_id, reports_to_user_id')
    .eq('organization_id', orgId);

  if (error) {
    console.warn('[organizationService.getMembers]', error.message, error.code);
    throw error;
  }
  if (!members?.length) return [];

  // 2. Workflow role names
  const roleIds = members.map(m => m.role_id).filter(Boolean) as string[];
  const roleNameMap = new Map<string, string>();
  if (roleIds.length > 0) {
    const { data: roles } = await supabase
      .from('workflow_roles_v9')
      .select('id, name')
      .in('id', roleIds);
    for (const r of roles ?? []) roleNameMap.set(r.id, r.name);
  }

  // 3. User profiles (try `profiles` first, fallback to `user_profiles`)
  const userIds = members.map(m => m.user_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, email, full_name')
    .in('id', userIds);
  const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

  // Fallback: fetch from user_profiles for users missing full_name
  const missingNameIds = userIds.filter(id => {
    const p = profileMap.get(id);
    return !p?.full_name;
  });
  if (missingNameIds.length > 0) {
    const { data: userProfiles } = await supabase
      .from('user_profiles')
      .select('user_id, name, email')
      .in('user_id', missingNameIds);
    for (const up of userProfiles ?? []) {
      const existing = profileMap.get(up.user_id);
      profileMap.set(up.user_id, {
        id: up.user_id,
        full_name: up.name || existing?.full_name || null,
        email: existing?.email || up.email || '',
      });
    }
  }

  // 4. Departments
  const deptIds = members.map(m => m.department_id).filter(Boolean) as string[];
  const deptMap = new Map<string, string>();
  if (deptIds.length > 0) {
    const { data: depts } = await supabase
      .from('org_departments')
      .select('id, name')
      .in('id', deptIds);
    for (const d of depts ?? []) deptMap.set(d.id, d.name);
  }

  // 5. Subordinate counts
  const subCounts = new Map<string, number>();
  for (const m of members) {
    if (m.reports_to_user_id) {
      subCounts.set(m.reports_to_user_id, (subCounts.get(m.reports_to_user_id) ?? 0) + 1);
    }
  }

  // 6. Active task counts
  const taskCountMap = new Map<string, number>();
  const { data: taskCounts } = await supabase
    .from('workflow_tasks_v9')
    .select('assigned_to')
    .eq('organization_id', orgId)
    .in('status', ['in_progress', 'todo', 'pending']);
  if (taskCounts) {
    for (const t of taskCounts) {
      if (t.assigned_to) taskCountMap.set(t.assigned_to, (taskCountMap.get(t.assigned_to) ?? 0) + 1);
    }
  }

  // 7. Late task counts
  const lateCountMap = new Map<string, number>();
  const { data: lateTasks } = await supabase
    .from('workflow_tasks_v9')
    .select('assigned_to')
    .eq('organization_id', orgId)
    .in('status', ['in_progress', 'todo', 'pending'])
    .eq('is_overdue', true);
  if (lateTasks) {
    for (const t of lateTasks) {
      if (t.assigned_to) lateCountMap.set(t.assigned_to, (lateCountMap.get(t.assigned_to) ?? 0) + 1);
    }
  }

  // 8. Build result
  return members.map(m => {
    const profile = profileMap.get(m.user_id);
    const reportsToProfile = m.reports_to_user_id ? profileMap.get(m.reports_to_user_id) : null;
    return {
      user_id: m.user_id,
      user_email: profile?.email ?? '',
      user_name: profile?.full_name ?? profile?.email?.split('@')[0] ?? 'Sem nome',
      role: m.role,
      role_type: (m.role_type as RoleType) ?? 'operational',
      role_id: m.role_id ?? null,
      role_name: m.role_id ? (roleNameMap.get(m.role_id) ?? null) : null,
      department_id: m.department_id,
      department_name: m.department_id ? (deptMap.get(m.department_id) ?? null) : null,
      reports_to_user_id: m.reports_to_user_id,
      reports_to_name: reportsToProfile?.full_name ?? reportsToProfile?.email?.split('@')[0] ?? null,
      subordinate_count: subCounts.get(m.user_id) ?? 0,
      active_task_count: taskCountMap.get(m.user_id) ?? 0,
      late_task_count: lateCountMap.get(m.user_id) ?? 0,
    };
  });
}

/**
 * Lightweight member list for search/autocomplete.
 * Returns name, email, department, role — no task counts.
 */
export async function getMembersLight(orgId: string): Promise<OrgMember[]> {
  const { data: members, error } = await supabase
    .from('user_organizations')
    .select('user_id, role, role_type, role_id, department_id, reports_to_user_id')
    .eq('organization_id', orgId);

  if (error) {
    console.warn('[organizationService.getMembersLight]', error.message, error.code);
    return [];
  }
  if (!members?.length) return [];

  const userIds = members.map(m => m.user_id);

  // Profiles
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, email, full_name')
    .in('id', userIds);
  const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

  // Fallback user_profiles
  const missingIds = userIds.filter(id => !profileMap.get(id)?.full_name);
  if (missingIds.length > 0) {
    const { data: ups } = await supabase
      .from('user_profiles')
      .select('user_id, name, email')
      .in('user_id', missingIds);
    for (const up of ups ?? []) {
      const existing = profileMap.get(up.user_id);
      profileMap.set(up.user_id, {
        id: up.user_id,
        full_name: up.name || existing?.full_name || null,
        email: existing?.email || up.email || '',
      });
    }
  }

  // Roles
  const roleIds = members.map(m => m.role_id).filter(Boolean) as string[];
  const roleMap = new Map<string, string>();
  if (roleIds.length > 0) {
    const { data: roles } = await supabase
      .from('workflow_roles_v9')
      .select('id, name')
      .in('id', roleIds);
    for (const r of roles ?? []) roleMap.set(r.id, r.name);
  }

  // Departments
  const deptIds = members.map(m => m.department_id).filter(Boolean) as string[];
  const deptMap = new Map<string, string>();
  if (deptIds.length > 0) {
    const { data: depts } = await supabase
      .from('org_departments')
      .select('id, name')
      .in('id', deptIds);
    for (const d of depts ?? []) deptMap.set(d.id, d.name);
  }

  return members.map(m => {
    const profile = profileMap.get(m.user_id);
    return {
      user_id: m.user_id,
      user_email: profile?.email ?? '',
      user_name: profile?.full_name ?? profile?.email?.split('@')[0] ?? 'Sem nome',
      role: m.role,
      role_type: (m.role_type as RoleType) ?? 'operational',
      role_id: m.role_id ?? null,
      role_name: m.role_id ? (roleMap.get(m.role_id) ?? null) : null,
      department_id: m.department_id,
      department_name: m.department_id ? (deptMap.get(m.department_id) ?? null) : null,
      reports_to_user_id: m.reports_to_user_id,
      reports_to_name: null,
      subordinate_count: 0,
      active_task_count: 0,
      late_task_count: 0,
    };
  });
}

/* =========================================================
   ADD / REMOVE MEMBER
========================================================= */

export async function addMember(params: {
  organization_id: string;
  user_id: string;
  role_type: RoleType;
  role_id?: string | null;
  department_id?: string | null;
  reports_to_user_id?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('user_organizations').insert({
    organization_id: params.organization_id,
    user_id: params.user_id,
    role: 'member',
    role_type: params.role_type,
    role_id: params.role_id || null,
    department_id: params.department_id || null,
    reports_to_user_id: params.reports_to_user_id || null,
  });
  if (error) throw error;
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('user_organizations')
    .delete()
    .eq('organization_id', orgId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function updateUserRole(orgId: string, userId: string, role: string): Promise<void> {
  const { error } = await supabase
    .from('user_organizations')
    .update({ role })
    .eq('organization_id', orgId)
    .eq('user_id', userId);
  if (error) throw error;
}

/* =========================================================
   UPDATE HIERARCHY
========================================================= */

export async function updateHierarchy(
  orgId: string,
  userId: string,
  updates: {
    role_type?: RoleType;
    role_id?: string | null;
    department_id?: string | null;
    reports_to_user_id?: string | null;
  },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.role_type !== undefined) payload.role_type = updates.role_type;
  if (updates.role_id !== undefined) payload.role_id = updates.role_id;
  if (updates.department_id !== undefined) payload.department_id = updates.department_id;
  if (updates.reports_to_user_id !== undefined) payload.reports_to_user_id = updates.reports_to_user_id;

  const { error } = await supabase
    .from('user_organizations')
    .update(payload)
    .eq('organization_id', orgId)
    .eq('user_id', userId);
  if (error) throw error;
}

/* =========================================================
   DEPARTMENTS
========================================================= */

export async function getDepartments(orgId: string): Promise<OrgDepartment[]> {
  const { data, error } = await supabase
    .from('org_departments')
    .select('*')
    .eq('organization_id', orgId)
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export async function createDepartment(orgId: string, name: string): Promise<void> {
  const { error } = await supabase
    .from('org_departments')
    .insert({ organization_id: orgId, name });
  if (error) throw error;
}

export async function updateDepartment(id: string, updates: { name?: string; color?: string }): Promise<void> {
  const { error } = await supabase
    .from('org_departments')
    .update(updates)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteDepartment(id: string): Promise<void> {
  const { error } = await supabase
    .from('org_departments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/* =========================================================
   ROLES (CARGOS)
========================================================= */

export async function getRoles(orgId: string): Promise<OrgRole[]> {
  const { data, error } = await supabase
    .from('workflow_roles_v9')
    .select('*')
    .eq('organization_id', orgId)
    .order('name');
  if (error) throw error;
  return data ?? [];
}

export async function createRole(orgId: string, name: string, departmentId?: string | null, description?: string): Promise<void> {
  const roleKey = name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  const { error } = await supabase
    .from('workflow_roles_v9')
    .insert({
      organization_id: orgId,
      name: name.trim(),
      role_key: roleKey,
      department_id: departmentId || null,
      description: description || null,
    });
  if (error) throw error;
}

export async function updateRole(id: string, updates: { name?: string; departmentId?: string }): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (updates.name !== undefined) payload.name = updates.name;
  if (updates.departmentId !== undefined) payload.department_id = updates.departmentId;
  const { error } = await supabase
    .from('workflow_roles_v9')
    .update(payload)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteRole(id: string): Promise<void> {
  const { error } = await supabase
    .from('workflow_roles_v9')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/* =========================================================
   PROFILES (for add-member modal)
========================================================= */

export async function getAvailableProfiles(existingUserIds: string[]): Promise<{ id: string; full_name: string | null; email: string }[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .order('full_name');
  if (error) throw error;
  const result = (data ?? []).filter(p => !existingUserIds.includes(p.id));

  // Enrich missing names from user_profiles
  const missingNameIds = result.filter(p => !p.full_name).map(p => p.id);
  if (missingNameIds.length > 0) {
    const { data: userProfiles } = await supabase
      .from('user_profiles')
      .select('user_id, name, email')
      .in('user_id', missingNameIds);
    const upMap = new Map((userProfiles ?? []).map(up => [up.user_id, up]));
    for (const p of result) {
      if (!p.full_name) {
        const up = upMap.get(p.id);
        if (up?.name) p.full_name = up.name;
        if (!p.email && up?.email) p.email = up.email;
      }
    }
  }

  return result;
}

/* =========================================================
   SCOPE (debug RPC)
========================================================= */

export async function getDebugScope(): Promise<{ user_id: string }[]> {
  const { data, error } = await supabase.rpc('debug_scope');
  if (error) throw error;
  return data ?? [];
}

/* =========================================================
   VALIDATION
========================================================= */

export interface MemberValidation {
  isComplete: boolean;
  missing: string[];
}

export function validateOrgMember(member: Pick<OrgMember, 'department_id' | 'role_id' | 'role_type' | 'reports_to_user_id'>): MemberValidation {
  const missing: string[] = [];
  if (!member.department_id) missing.push('department');
  if (!member.role_id) missing.push('role');
  if (member.role_type !== 'director' && !member.reports_to_user_id) missing.push('manager');
  return { isComplete: missing.length === 0, missing };
}
